import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ExposicaoFeiraContext } from 'contexts/ExposiçãoFeiraContext';
import { RemessasContext } from 'contexts/RemessasContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import TipoModal from './Modals/TipoModal';
import columnOverrides from './columnOverrides';
import getExposicaoFeiraFilterFields from './filterFields';

const ExposiçãoFeira = () => {
  const rotina = 'RemessasExposiçãoFeira';
  const titulo = 'Exposições / Feiras';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { postRemessaCopia } = useContext(RemessasContext);
  const { exposicaoFeira, getExposicaoFeira, getLoading } = useContext(
    ExposicaoFeiraContext
  );

  const filterFields = useMemo(
    () => getExposicaoFeiraFilterFields(drops),
    [drops]
  );

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getExposicaoFeira({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Adicionar',
      icon: 'add',
      placement: 'primary',
      action: () =>
        openModal(
          <TipoModal
            onSubmit={(url) => navigate(`/app/Remessas/Gerar${url}`)}
          />
        ),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({
          rotina,
          filter: getValues(),
          order: exposicaoFeira?.order,
        }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({
          rotina,
          filter: getValues(),
          order: exposicaoFeira?.order,
        }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={exposicaoFeira?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
  ];

  const rowActions = [
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Copiar Remessa',
      icon: 'sync_problem',
      menu: true,
      action: ({ row }) =>
        postRemessaCopia({ data: { documento_id: row?.id }, cb: loadGrid }),
    },
    {
      name: 'Resumo da Remessa',
      icon: 'summarize',
      menu: true,
      action: ({ row }) => navigate(`/app/Remessas/Resumo/${row?.id}`),
    },
    {
      name: 'Retornar Remessa',
      icon: 'undo',
      menu: true,
      show: ({ row }) => row?.situacao !== 'ENCERRADO',
      action: ({ row }) => navigate(`/app/Remessas/Retorno/${row?.id}`),
    },
  ];

  return (
    <Container fillHeight>
      <Header
        rotina={rotina}
        colunas={exposicaoFeira?.colunas}
        titulo={titulo}
      />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={exposicaoFeira}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default ExposiçãoFeira;
